import React, { useState, useEffect } from 'react';
import API from '../api';
import { SellerSpinner } from '../components/skeleton';
import SellerIngestionPanel from '../components/SellerIngestionPanel';
import '../styles/SellerCommon.css';
import '../styles/SellerIntegration.css';

function SellerIntegration() {
  const [sellerInfo, setSellerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [editingSection, setEditingSection] = useState(null);
  const [updatingCatalog, setUpdatingCatalog] = useState(false);

  // Eliminazione integrazione (Danger zone)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedIntegration, setSelectedIntegration] = useState('');
  const [focusedTokenField, setFocusedTokenField] = useState(null);
  const [wizardFormData, setWizardFormData] = useState({
    integrationMethod: '',
    feedUrl: '',
    shopifyUrl: '',
    shopifyApiToken: '',
    shopifyProductBaseUrl: '',
    prestashopUrl: '',
    prestashopApiToken: '',
    prestashopProductBaseUrl: '',
    woocommerceUrl: '',
    woocommerceApiKey: '',
    woocommerceApiSecret: '',
    woocommerceProductBaseUrl: '',
    magentoUrl: '',
    magentoAccessToken: '',
    magentoProductBaseUrl: ''
  });
  
  const [formData, setFormData] = useState({
    integrationMethod: '',
    feedUrl: '',
    shopifyUrl: '',
    shopifyApiToken: '',
    shopifyProductBaseUrl: '',
    prestashopUrl: '',
    prestashopApiToken: '',
    prestashopProductBaseUrl: '',
    woocommerceUrl: '',
    woocommerceApiKey: '',
    woocommerceApiSecret: '',
    woocommerceProductBaseUrl: '',
    magentoUrl: '',
    magentoAccessToken: '',
    magentoProductBaseUrl: ''
  });

  useEffect(() => {
    fetchSellerInfo();
  }, []);

  const fetchSellerInfo = async () => {
    try {
      const response = await API.get('/auth/seller-additional-info');
      if (response.data.success) {
        setSellerInfo(response.data.sellerInfo);
        setFormData({
          integrationMethod: response.data.sellerInfo?.integrationMethod || '',
          feedUrl: response.data.sellerInfo?.feedUrl || '',
          shopifyUrl: response.data.sellerInfo?.shopifyUrl || '',
          shopifyApiToken: response.data.sellerInfo?.shopifyApiToken || '',
          shopifyProductBaseUrl: response.data.sellerInfo?.shopifyProductBaseUrl || '',
          prestashopUrl: response.data.sellerInfo?.prestashopUrl || '',
          prestashopApiToken: response.data.sellerInfo?.prestashopApiToken || '',
          prestashopProductBaseUrl: response.data.sellerInfo?.prestashopProductBaseUrl || '',
          woocommerceUrl: response.data.sellerInfo?.woocommerceUrl || '',
          woocommerceApiKey: response.data.sellerInfo?.woocommerceApiKey || '',
          woocommerceApiSecret: response.data.sellerInfo?.woocommerceApiSecret || '',
          woocommerceProductBaseUrl: response.data.sellerInfo?.woocommerceProductBaseUrl || '',
          magentoUrl: response.data.sellerInfo?.magentoUrl || '',
          magentoAccessToken: response.data.sellerInfo?.magentoAccessToken || '',
          magentoProductBaseUrl: response.data.sellerInfo?.magentoProductBaseUrl || ''
        });
      }
    } catch (error) {
      console.error('Errore nel recupero delle informazioni del venditore:', error);
      setMessage('Errore nel caricamento delle informazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Custom handler for token fields in existing form
  const handleTokenChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSaveIntegration = async () => {
    try {
      const response = await API.post('/auth/update-seller-integration-info', formData);
      if (response.data.success) {
        setMessage('Configurazione salvata con successo!');
        setEditingSection(null);
        fetchSellerInfo();
      } else {
        setMessage('Errore nel salvataggio della configurazione');
      }
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      setMessage('Errore nel salvataggio della configurazione');
    }
  };

  const handleClearIntegration = () => {
    setFormData({
      integrationMethod: '',
      feedUrl: '',
      shopifyUrl: '',
      shopifyApiToken: '',
      shopifyProductBaseUrl: '',
      prestashopUrl: '',
      prestashopApiToken: '',
      prestashopProductBaseUrl: '',
      woocommerceUrl: '',
      woocommerceApiKey: '',
      woocommerceApiSecret: '',
      woocommerceProductBaseUrl: '',
      magentoUrl: '',
      magentoAccessToken: '',
      magentoProductBaseUrl: ''
    });
    setEditingSection(null);
  };

  const DELETE_CONFIRM_WORD = 'ELIMINA';

  const closeDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setDeleteConfirmText('');
  };

  const handleDeleteIntegration = async () => {
    if (deleteConfirmText.trim() !== DELETE_CONFIRM_WORD) return;

    setDeleting(true);
    setMessage('');

    try {
      const response = await API.post('/auth/delete-seller-integration');
      if (response.data.success) {
        setShowDeleteModal(false);
        setDeleteConfirmText('');
        setMessage('Integrazione eliminata con successo!');
        fetchSellerInfo();
      } else {
        setMessage('Errore nell\'eliminazione dell\'integrazione');
      }
    } catch (error) {
      console.error('Errore nell\'eliminazione dell\'integrazione:', error);
      const apiMessage = error.response?.data?.message;
      setMessage(apiMessage || 'Errore nell\'eliminazione dell\'integrazione');
    } finally {
      setDeleting(false);
    }
  };

  // Check if seller has an active integration
  const hasIntegration = () => {
    if (!sellerInfo) return false;
    
    switch (sellerInfo.integrationMethod) {
      case 'feed':
        return !!sellerInfo.feedUrl;
      case 'shopify':
        return !!sellerInfo.shopifyUrl && !!sellerInfo.shopifyApiToken;
      case 'prestashop':
        return !!sellerInfo.prestashopUrl && !!sellerInfo.prestashopApiToken;
      case 'woocommerce':
        return !!sellerInfo.woocommerceUrl && !!sellerInfo.woocommerceApiKey && !!sellerInfo.woocommerceApiSecret;
      case 'magento':
        return !!sellerInfo.magentoUrl && !!sellerInfo.magentoAccessToken;
      default:
        return false;
    }
  };

  const canUpdateCatalog = () => {
    return hasIntegration();
  };

  const getUpdateStatusInfo = () => {
    if (!sellerInfo) return { status: 'idle', message: 'Caricamento...' };

    const now = new Date();
    const lastUpdate = sellerInfo.last_import_end ? new Date(sellerInfo.last_import_end) : null;
    const frequency = sellerInfo.import_frequency || 24;
    const frequencyMs = frequency * 60 * 60 * 1000;

    if (sellerInfo.import_status === 'in_progress') {
      return { status: 'running', message: 'Aggiornamento in corso...' };
    }

    if (sellerInfo.import_status === 'error') {
      return { status: 'error', message: 'Errore nell\'ultimo aggiornamento' };
    }

    if (!lastUpdate) {
      return { status: 'pending', message: 'Nessun aggiornamento effettuato' };
    }

    const timeSinceLastUpdate = now - lastUpdate;
    if (timeSinceLastUpdate > frequencyMs) {
      return { status: 'pending', message: 'Puoi richiedere un aggiornamento (max 30 minuti)' };
    }

    const nextUpdate = new Date(lastUpdate.getTime() + frequencyMs);
    const timeUntilNext = nextUpdate - now;
    const hoursUntilNext = Math.ceil(timeUntilNext / (1000 * 60 * 60));

    return { 
      status: 'success', 
      message: `Prossimo aggiornamento tra ${hoursUntilNext} ore` 
    };
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Mai';
    
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} giorn${diffDays > 1 ? 'i' : 'o'} fa`;
    } else if (diffHours > 0) {
      return `${diffHours} or${diffHours > 1 ? 'e' : 'a'} fa`;
    } else {
      return 'Pochi minuti fa';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'in_progress': return '🔄 In Corso';
      case 'completed': return '✅ Completato';
      case 'error': return '❌ Errore';
      case 'idle': 
      default: return '⏸️ In Attesa';
    }
  };

  const handleUpdateCatalog = async () => {
    if (!canUpdateCatalog() || updatingCatalog) return;

    setUpdatingCatalog(true);
    setMessage('');

    try {
      const response = await API.post('/auth/trigger-catalog-update');
      if (response.data.success) {
        setMessage('Aggiornamento catalogo avviato con successo!');
        // Polling per aggiornare lo status
        setTimeout(fetchSellerInfo, 2000);
      } else {
        setMessage('Errore nell\'avvio dell\'aggiornamento');
      }
    } catch (error) {
      console.error('Errore nell\'aggiornamento:', error);
      setMessage('Errore nell\'aggiornamento del catalogo');
    } finally {
      setUpdatingCatalog(false);
    }
  };

  // Funzione per controllare se il pulsante "Aggiorna Catalogo" deve essere disabilitato
  const isUpdateButtonDisabled = () => {
    if (!sellerInfo) return true;
    
    // Se l'import è in corso, disabilita
    if (sellerInfo.import_status === 'in_progress') return true;
    
    // Se l'ultimo import è stato completato meno di 30 minuti fa, disabilita
    if (sellerInfo.last_import_end) {
      const lastImport = new Date(sellerInfo.last_import_end);
      const now = new Date();
      const minutesSinceLastImport = (now - lastImport) / (1000 * 60);
      if (minutesSinceLastImport < 30) return true;
    }
    
    return false;
  };

  // Funzione per ottenere il messaggio di stato del pulsante
  const getUpdateButtonMessage = () => {
    if (!sellerInfo) return 'Caricamento...';
    
    if (sellerInfo.import_status === 'in_progress') {
      return 'Aggiornamento in corso...';
    }
    
    if (sellerInfo.last_import_end) {
      const lastImport = new Date(sellerInfo.last_import_end);
      const now = new Date();
      const minutesSinceLastImport = (now - lastImport) / (1000 * 60);
      if (minutesSinceLastImport < 30) {
        const remainingMinutes = Math.ceil(30 - minutesSinceLastImport);
        return `Disponibile tra ${remainingMinutes} minuti`;
      }
    }
    
    return 'Aggiorna Catalogo';
  };

  // Wizard functions
  const handleWizardStepChange = (step) => {
    setWizardStep(step);
  };

  const handleWizardIntegrationSelect = (integration) => {
    setSelectedIntegration(integration);
    setWizardFormData(prev => ({ ...prev, integrationMethod: integration }));
    setWizardStep(2);
  };

  const handleWizardFormChange = (e) => {
    setWizardFormData({ ...wizardFormData, [e.target.name]: e.target.value });
  };

  // Custom handler for token fields to show last 4 characters
  const handleWizardTokenChange = (e) => {
    const { name, value } = e.target;
    setWizardFormData({ ...wizardFormData, [name]: value });
  };

  // Function to format token display (show last 4 characters)
  const formatTokenDisplay = (value) => {
    if (!value) return '';
    if (value.length <= 4) return value;
    return '•'.repeat(value.length - 4) + value.slice(-4);
  };

  // Custom Token Input Component
  const TokenInput = ({ name, value, onChange, placeholder, label, type = "password" }) => {
    const isFocused = focusedTokenField === name;
    const displayValue = isFocused ? value : formatTokenDisplay(value);
    
    return (
      <div className="seller-form-group">
        <label className="seller-form-label">{label}</label>
        <input
          type={isFocused ? "text" : "password"}
          className="seller-form-control"
          name={name}
          value={displayValue}
          onChange={onChange}
          onFocus={() => setFocusedTokenField(name)}
          onBlur={() => setFocusedTokenField(null)}
          placeholder={placeholder}
        />
      </div>
    );
  };

  const handleWizardSave = async () => {
    try {
      const response = await API.post('/auth/update-seller-integration-info', wizardFormData);
      if (response.data.success) {
        setMessage('Integrazione configurata con successo!');
        setWizardStep(4);
        fetchSellerInfo();
      } else {
        setMessage('Errore nel salvataggio della configurazione');
      }
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      setMessage('Errore nel salvataggio della configurazione');
    }
  };

  const handleWizardStartIntegration = async () => {
    try {
      setUpdatingCatalog(true);
      setMessage('');
      
      const response = await API.post('/auth/trigger-catalog-update');
      if (response.data.success) {
        setMessage('Integrazione avviata con successo!');
        setWizardStep(5);
        setTimeout(fetchSellerInfo, 2000);
      } else {
        setMessage('Errore nell\'avvio dell\'integrazione');
      }
    } catch (error) {
      console.error('Errore nell\'avvio dell\'integrazione:', error);
      setMessage('Errore nell\'avvio dell\'integrazione');
    } finally {
      setUpdatingCatalog(false);
    }
  };

  if (loading) return <SellerSpinner text="Caricamento integrazione..." />;

  const updateStatus = getUpdateStatusInfo();
  const hasActiveIntegration = hasIntegration();

  // API Push (Ingestion API): mostra il pannello dedicato sia se l'utente
  // ha appena selezionato 'push' dal wizard sia se è già la sua integrazione attiva.
  const showPushPanel =
    sellerInfo?.integrationMethod === 'push' || selectedIntegration === 'push';
  if (showPushPanel) {
    return (
      <>
        <div className="seller-page-header">
          <h1>Integrazione API Push</h1>
          <p>Invia il catalogo a Loverlist tramite chiamate REST in tempo reale</p>
        </div>
        <SellerIngestionPanel sellerInfo={sellerInfo} onActivated={fetchSellerInfo} />
      </>
    );
  }

  // Show wizard if no integration exists
  if (!hasActiveIntegration) {
    return (
      <>
        <div className="seller-page-header">
          <h1>Integrazione CMS</h1>
          <p>Configura l'integrazione con il tuo e-commerce</p>
        </div>

        {message && (
          <div className={`seller-message ${message.includes('successo') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}

        {/* Wizard Steps */}
        <div className="seller-card">
          <div className="seller-wizard-steps">
            <div className={`seller-wizard-step ${wizardStep >= 1 ? 'active' : ''}`}>
              <div className="seller-wizard-step-number">1</div>
              <div className="seller-wizard-step-title">Scegli l'integrazione</div>
            </div>
            <div className={`seller-wizard-step ${wizardStep >= 2 ? 'active' : ''}`}>
              <div className="seller-wizard-step-number">2</div>
              <div className="seller-wizard-step-title">Guida API</div>
            </div>
            <div className={`seller-wizard-step ${wizardStep >= 3 ? 'active' : ''}`}>
              <div className="seller-wizard-step-number">3</div>
              <div className="seller-wizard-step-title">Inserisci i dati</div>
            </div>
            <div className={`seller-wizard-step ${wizardStep >= 4 ? 'active' : ''}`}>
              <div className="seller-wizard-step-number">4</div>
              <div className="seller-wizard-step-title">Avvia integrazione</div>
            </div>
          </div>

          {/* Step 1: Choose Integration */}
          {wizardStep === 1 && (
            <div className="seller-wizard-content">
              <h3>Scegli il tuo sistema e-commerce</h3>
              <p>Seleziona la piattaforma che utilizzi per il tuo negozio online:</p>
              <div className="seller-integration-options">
                <div className="seller-integration-option" onClick={() => handleWizardIntegrationSelect('shopify')}>
                  <div className="seller-integration-option-icon">🛍️</div>
                  <div className="seller-integration-option-content">
                    <h4>Shopify</h4>
                    <p>Integrazione con negozi Shopify</p>
                  </div>
                </div>
                <div className="seller-integration-option" onClick={() => handleWizardIntegrationSelect('prestashop')}>
                  <div className="seller-integration-option-icon">🛒</div>
                  <div className="seller-integration-option-content">
                    <h4>PrestaShop</h4>
                    <p>Integrazione con negozi PrestaShop</p>
                  </div>
                </div>
                <div className="seller-integration-option" onClick={() => handleWizardIntegrationSelect('woocommerce')}>
                  <div className="seller-integration-option-icon">📦</div>
                  <div className="seller-integration-option-content">
                    <h4>WooCommerce</h4>
                    <p>Integrazione con negozi WooCommerce</p>
                  </div>
                </div>
                <div className="seller-integration-option" onClick={() => handleWizardIntegrationSelect('magento')}>
                  <div className="seller-integration-option-icon">🏪</div>
                  <div className="seller-integration-option-content">
                    <h4>Magento 2</h4>
                    <p>Integrazione con negozi Magento 2</p>
                  </div>
                </div>
                <div className="seller-integration-option" onClick={() => handleWizardIntegrationSelect('feed')}>
                  <div className="seller-integration-option-icon">📄</div>
                  <div className="seller-integration-option-content">
                    <h4>Feed XML/CSV</h4>
                    <p>Integrazione tramite feed di prodotti</p>
                  </div>
                </div>
                <div className="seller-integration-option" onClick={() => handleWizardIntegrationSelect('push')}>
                  <div className="seller-integration-option-icon">⚡</div>
                  <div className="seller-integration-option-content">
                    <h4>API Push <small>(avanzato)</small></h4>
                    <p>Invio diretto via REST con propagazione real-time</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: API Guide */}
          {wizardStep === 2 && (
            <div className="seller-wizard-content">
              <h3>Guida per creare la chiave API</h3>
              {selectedIntegration === 'shopify' && (
                <div className="seller-api-guide">
                  <h4>Come creare l'app privata Shopify per Loverlist:</h4>
                  <ol>
                    <li>Accedi al tuo pannello di controllo Shopify</li>
                    <li>Vai su <strong>Impostazioni</strong> → <strong>App e canali</strong></li>
                    <li>Clicca su <strong>Sviluppa app</strong></li>
                    <li>Clicca su <strong>Crea un'app</strong></li>
                    <li>Inserisci i seguenti dati:
                      <ul>
                        <li><strong>Nome app:</strong> loverlist</li>
                        <li><strong>Email sviluppatore:</strong> info@loverlist.com</li>
                      </ul>
                    </li>
                    <li>Clicca su <strong>Crea app</strong></li>
                    <li>Nella sezione <strong>Configurazione API</strong>, clicca su <strong>Configura</strong></li>
                    <li>Configura le seguenti autorizzazioni:
                      <ul>
                        <li><strong>Prodotti:</strong> <code>read_products</code></li>
                      </ul>
                    </li>
                    <li>Clicca su <strong>Salva</strong></li>
                    <li>Torna alla pagina principale dell'app e clicca su <strong>Installa app</strong></li>
                    <li>Conferma l'installazione cliccando su <strong>Installa app</strong></li>
                    <li>Una volta installata, clicca su <strong>Mostra token</strong> (solo una volta!)</li>
                    <li>Copia il <strong>Token di accesso</strong> generato (inizia con <code>shpat_</code>)</li>
                  </ol>
                  <div className="seller-api-guide-note">
                    <strong>⚠️ Importante:</strong> Il token viene mostrato solo una volta. Se lo perdi, dovrai rigenerarlo.
                  </div>
                </div>
              )}
              {selectedIntegration === 'prestashop' && (
                <div className="seller-api-guide">
                  <h4>Come ottenere il token API di PrestaShop:</h4>
                  <ol>
                    <li>Accedi al back-office di PrestaShop</li>
                    <li>Vai su <strong>Prametri Avanzati</strong> → <strong>Web Service</strong></li>
                    <li>Attiva servizi <strong>Web Service</strong> se non lo è già</li>
                    <li>Clicca su <strong>Aggiungi</strong> per creare una nuova chiave</li>
                    <li>Clicca su <strong>Genera</strong> per generare il token</li>
                    <li>Configura i permessi di lettura per <strong>Prodotti</strong></li>
                    <li>Copia la <strong>Chiave API</strong> generata</li> 
                  </ol>
                </div>
              )}
              {selectedIntegration === 'woocommerce' && (
                <div className="seller-api-guide">
                  <h4>Come ottenere le chiavi API di WooCommerce:</h4>
                  <ol>
                    <li>Accedi al pannello di controllo WordPress</li>
                    <li>Vai su <strong>WooCommerce</strong> → <strong>Impostazioni</strong> → <strong>Avanzate</strong> → <strong>REST API</strong></li>
                    <li>Clicca su <strong>Aggiungi chiave</strong></li>
                    <li>Come descrizione della chiave inserisci <strong>Loverlist</strong></li>
                    <li>Come utente scegli l'admin</li>
                    <li>Configura i permessi per <strong>Lettura</strong></li>
                    <li>Clicca su <strong>Genera chiave API</strong></li>
                    <li>Copia la <strong>Chiave utente</strong> e la chiave <strong>Utente nascosto</strong></li>
                  </ol>
                </div>
              )}
              {selectedIntegration === 'magento' && (
                <div className="seller-api-guide">
                  <h4>Come ottenere il token di accesso di Magento:</h4>
                  <ol>
                    <li>Accedi al pannello di controllo Magento</li>
                    <li>Vai su <strong>Sistema</strong> → <strong>Integrazioni</strong></li>
                    <li>Clicca su <strong>Aggiungi nuova integrazione</strong></li>
                    <li>Nella sezione <strong>Generale</strong> inserisci nome "Loverlist"</li>
                    <li>Nella sezione <strong>Risorse</strong> seleziona <strong>Catalogo</strong></li>
                    <li>Copia il <strong>Token di accesso</strong> generato</li>
                  </ol>
                </div>
              )}
              {selectedIntegration === 'feed' && (
                <div className="seller-api-guide">
                  <h4>Come preparare il feed XML/CSV:</h4>
                  <ol>
                    <li>Assicurati che il feed sia accessibile via URL pubblico</li>
                    <li>Il feed deve contenere almeno: nome prodotto, prezzo, descrizione</li>
                    <li>Formati supportati: XML, CSV, JSON</li>
                    <li>Il feed deve essere aggiornato regolarmente</li>
                  </ol>
                </div>
              )}
              <div className="seller-wizard-actions">
                <button onClick={() => setWizardStep(1)} className="seller-btn seller-btn-secondary">Indietro</button>
                <button onClick={() => setWizardStep(3)} className="seller-btn seller-btn-primary">Avanti</button>
              </div>
            </div>
          )}

          {/* Step 3: Enter Data */}
          {wizardStep === 3 && (
            <div className="seller-wizard-content">
              <h3>Inserisci i dati di configurazione</h3>
              <form className="seller-integration-form">
                {selectedIntegration === 'shopify' && (
                  <>
                    <div className="seller-form-group">
                      <label className="seller-form-label">Shopify URL:</label>
                      <input type="url" className="seller-form-control" name="shopifyUrl" value={wizardFormData.shopifyUrl} onChange={handleWizardFormChange} placeholder="tuo-negozio.myshopify.com" />
                    </div>
                      <TokenInput
                        name="shopifyApiToken"
                        value={wizardFormData.shopifyApiToken}
                        onChange={handleWizardTokenChange}
                        placeholder="shpat_xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        label="Shopify API Token:"
                      />
                    <div className="seller-form-group">
                      <label className="seller-form-label">URL di base prodotti:</label>
                      <input type="text" className="seller-form-control" name="shopifyProductBaseUrl" value={wizardFormData.shopifyProductBaseUrl} onChange={handleWizardFormChange} placeholder="https://tuo-negozio.com" />
                    </div>
                  </>
                )}
                {selectedIntegration === 'prestashop' && (
                  <>
                    <div className="seller-form-group">
                      <label className="seller-form-label">PrestaShop URL:</label>
                      <input type="url" className="seller-form-control" name="prestashopUrl" value={wizardFormData.prestashopUrl} onChange={handleWizardFormChange} placeholder="https://tuo-negozio.com" />
                    </div>
                      <TokenInput
                        name="prestashopApiToken"
                        value={wizardFormData.prestashopApiToken}
                        onChange={handleWizardTokenChange}
                        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        label="PrestaShop API Token:"
                      />
                    <div className="seller-form-group">
                      <label className="seller-form-label">URL di base prodotti:</label>
                      <input type="text" className="seller-form-control" name="prestashopProductBaseUrl" value={wizardFormData.prestashopProductBaseUrl} onChange={handleWizardFormChange} placeholder="https://tuo-negozio.com/prodotto" />
                    </div>
                  </>
                )}
                {selectedIntegration === 'woocommerce' && (
                  <>
                    <div className="seller-form-group">
                      <label className="seller-form-label">WooCommerce URL:</label>
                      <input type="url" className="seller-form-control" name="woocommerceUrl" value={wizardFormData.woocommerceUrl} onChange={handleWizardFormChange} placeholder="https://tuo-negozio.com" />
                    </div>
                                          <TokenInput
                        name="woocommerceApiKey"
                        value={wizardFormData.woocommerceApiKey}
                        onChange={handleWizardTokenChange}
                        placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        label="WooCommerce API Key:"
                        type="text"
                      />
                      <TokenInput
                        name="woocommerceApiSecret"
                        value={wizardFormData.woocommerceApiSecret}
                        onChange={handleWizardTokenChange}
                        placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        label="WooCommerce API Secret:"
                      />
                    <div className="seller-form-group">
                      <label className="seller-form-label">URL di base prodotti:</label>
                      <input type="text" className="seller-form-control" name="woocommerceProductBaseUrl" value={wizardFormData.woocommerceProductBaseUrl} onChange={handleWizardFormChange} placeholder="https://tuo-negozio.com/prodotto" />
                    </div>
                  </>
                )}
                {selectedIntegration === 'magento' && (
                  <>
                    <div className="seller-form-group">
                      <label className="seller-form-label">Magento URL:</label>
                      <input type="url" className="seller-form-control" name="magentoUrl" value={wizardFormData.magentoUrl} onChange={handleWizardFormChange} placeholder="https://tuo-negozio.com" />
                    </div>
                      <TokenInput
                        name="magentoAccessToken"
                        value={wizardFormData.magentoAccessToken}
                        onChange={handleWizardTokenChange}
                        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        label="Magento Access Token:"
                      />
                    <div className="seller-form-group">
                      <label className="seller-form-label">URL di base prodotti:</label>
                      <input type="text" className="seller-form-control" name="magentoProductBaseUrl" value={wizardFormData.magentoProductBaseUrl} onChange={handleWizardFormChange} placeholder="https://tuo-negozio.com/prodotto" />
                    </div>
                  </>
                )}
                {selectedIntegration === 'feed' && (
                  <div className="seller-form-group">
                    <label className="seller-form-label">Feed URL:</label>
                    <input type="url" className="seller-form-control" name="feedUrl" value={wizardFormData.feedUrl} onChange={handleWizardFormChange} placeholder="https://esempio.com/feed.xml" />
                  </div>
                )}
                <div className="seller-wizard-actions">
                  <button type="button" onClick={() => setWizardStep(2)} className="seller-btn seller-btn-secondary">Indietro</button>
                  <button type="button" onClick={handleWizardSave} className="seller-btn seller-btn-success">Salva Configurazione</button>
                </div>
              </form>
            </div>
          )}

          {/* Step 4: Start Integration */}
          {wizardStep === 4 && (
            <div className="seller-wizard-content">
              <h3>Avvia l'integrazione</h3>
              <p>La configurazione è stata salvata con successo! Ora puoi avviare l'importazione dei tuoi prodotti.</p>
              <div className="seller-wizard-actions">
                <button onClick={handleWizardStartIntegration} disabled={updatingCatalog} className="seller-btn seller-btn-primary">{updatingCatalog ? 'Avvio in corso...' : 'Avvia Integrazione'}</button>
              </div>
            </div>
          )}

          {/* Step 5: Integration Complete */}
          {wizardStep === 5 && (
            <div className="seller-wizard-content">
              <h3>✅ Integrazione completata!</h3>
              <p>L'integrazione è stata avviata con successo. I tuoi prodotti verranno importati automaticamente.</p>
              <div className="seller-wizard-actions">
                <button onClick={() => window.location.reload()} className="seller-btn seller-btn-primary">Vai alla Dashboard</button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  // Show existing integration interface
  return (
    <>
      <div className="seller-page-header">
        <h1>Integrazione CMS</h1>
        <p>Configura l'integrazione con il tuo e-commerce</p>
      </div>
      {message && (
        <div className={`seller-message ${message.includes('successo') ? 'success' : 'error'}`}>{message}</div>
      )}

      {/* CTA per esplorare API Push (avanzato) — non forza il switch */}
      <div className="seller-card seller-push-cta-card">
        <div className="seller-push-cta-content">
          <div className="seller-push-cta-icon">⚡</div>
          <div className="seller-push-cta-text">
            <h4>Vuoi propagare prodotti in tempo reale?</h4>
            <p>
              Con la modalità <strong>API Push</strong> invii direttamente prodotti, prezzi e
              stock senza aspettare il cron giornaliero. Ideale se hai un team dev o un sistema
              custom che vuole controllare la sincronizzazione.
              <br />
              <small>
                Nota: l'attivazione di API Push sostituisce il metodo attuale ({sellerInfo?.integrationMethod}).
                Puoi comunque generare chiavi <em>test</em> per provare senza disattivare l'integrazione corrente.
              </small>
            </p>
          </div>
          <button
            className="seller-btn seller-btn-secondary"
            onClick={() => setSelectedIntegration('push')}
          >
            Esplora API Push
          </button>
        </div>
      </div>

      {/* Status Aggiornamento Catalogo */}
      <div className="seller-card">
        <div className="seller-card-header">
          <h3>Status Aggiornamento Catalogo</h3>
          <button onClick={handleUpdateCatalog} disabled={isUpdateButtonDisabled()} className={`seller-btn seller-btn-primary ${(!canUpdateCatalog() || updatingCatalog) ? 'disabled' : ''}`}>{getUpdateButtonMessage()}</button>
        </div>

        <div className="seller-integration-stats-grid">
          {/* Status Corrente */}
          <div className="seller-integration-stat-card">
            <div className="seller-integration-stat-label">Status Corrente</div>
            <div className={`seller-integration-stat-value seller-integration-status-${sellerInfo?.import_status || 'idle'}`}>
              {getStatusText(sellerInfo?.import_status)}
            </div>
          </div>

          {/* Ultimo Aggiornamento */}
          <div className="seller-integration-stat-card">
            <div className="seller-integration-stat-label">Ultimo Aggiornamento</div>
            <div className="seller-integration-stat-value">
              {formatTimeAgo(sellerInfo?.last_import_end)}
            </div>
          </div>

          {/* Durata Ultimo Import */}
          <div className="seller-integration-stat-card">
            <div className="seller-integration-stat-label">Durata Ultimo Import</div>
            <div className="seller-integration-stat-value">
              {sellerInfo?.last_import_start && sellerInfo?.last_import_end ? (
                (() => {
                  const start = new Date(sellerInfo.last_import_start);
                  const end = new Date(sellerInfo.last_import_end);
                  const duration = Math.floor((end - start) / (1000 * 60));
                  return `${duration} minuti`;
                })()
              ) : 'N/A'}
            </div>
          </div>

          {/* Frequenza Aggiornamento */}
          <div className="seller-integration-stat-card">
            <div className="seller-integration-stat-label">Frequenza</div>
            <div className="seller-integration-stat-value">
              {sellerInfo?.import_frequency || 24} ore
            </div>
          </div>
        </div>

        {/* Messaggio Status */}
        <div className={`seller-integration-status-message ${updateStatus.status}`}>
          <strong>{updateStatus.message}</strong>
        </div>

        {/* Errori Recenti */}
        {sellerInfo?.import_error && (
          <div className="seller-integration-error-message">
            <strong>⚠️ Errore Ultimo Import:</strong> {sellerInfo.import_error}
          </div>
        )}
      </div>

      {/* Configurazione Integrazione */}
      <div className="seller-card">
        <div className="seller-card-header">
          <h3>Configurazione Integrazione</h3>
          <button
            onClick={() => setEditingSection(editingSection === 'integration' ? null : 'integration')}
            className={`seller-btn seller-btn-secondary ${editingSection === 'integration' ? 'seller-btn-danger' : ''}`}
          >
            {editingSection === 'integration' ? 'Annulla' : 'Modifica'}
          </button>
        </div>

        {editingSection === 'integration' ? (
          <form className="seller-integration-form">
            {/* Metodo di integrazione */}
            <div className="seller-form-group">
              <label className="seller-form-label">
                Metodo di Integrazione:
              </label>
              <select
                className="seller-form-control"
                name="integrationMethod"
                value={formData.integrationMethod}
                onChange={handleChange}
              >
                <option value="">Seleziona un metodo</option>
                <option value="feed">Feed XML/CSV</option>
                <option value="shopify">Shopify</option>
                <option value="prestashop">PrestaShop</option>
                <option value="woocommerce">WooCommerce</option>
                <option value="magento">Magento 2</option>
              </select>
            </div>

            {/* Campi Condizionali */}
            {formData.integrationMethod === 'feed' && (
              <div className="seller-form-group">
                <label className="seller-form-label">
                  Feed URL:
                </label>
                <input
                  type="url"
                  className="seller-form-control"
                  name="feedUrl"
                  value={formData.feedUrl}
                  onChange={handleChange}
                  placeholder="https://esempio.com/feed.xml"
                />
              </div>
            )}

            {formData.integrationMethod === 'shopify' && (
              <div className="seller-integration-form-group">
                <div className="seller-form-group">
                  <label className="seller-form-label">
                    Shopify URL:
                  </label>
                  <input
                    type="url"
                    className="seller-form-control"
                    name="shopifyUrl"
                    value={formData.shopifyUrl}
                    onChange={handleChange}
                    placeholder="https://tuo-negozio.myshopify.com"
                  />
                </div>
                <TokenInput
                  name="shopifyApiToken"
                  value={formData.shopifyApiToken}
                  onChange={handleTokenChange}
                  placeholder="shpat_xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  label="Shopify API Token:"
                />
                <div className="seller-form-group">
                  <label className="seller-form-label">
                    Dominio Shopify:
                  </label>
                  <input
                    type="text"
                    className="seller-form-control"
                    name="shopifyProductBaseUrl"
                    value={formData.shopifyProductBaseUrl}
                    onChange={handleChange}
                    placeholder="https://tuo-negozio.com"
                  />
                </div>
              </div>
            )}

            {formData.integrationMethod === 'prestashop' && (
              <div className="seller-integration-form-group">
                <div className="seller-form-group">
                  <label className="seller-form-label">
                    PrestaShop URL:
                  </label>
                  <input
                    type="url"
                    className="seller-form-control"
                    name="prestashopUrl"
                    value={formData.prestashopUrl}
                    onChange={handleChange}
                    placeholder="https://tuo-negozio.com"
                  />
                </div>
                <TokenInput
                  name="prestashopApiToken"
                  value={formData.prestashopApiToken}
                  onChange={handleTokenChange}
                  placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  label="PrestaShop API Token:"
                />
                <div className="seller-form-group">
                  <label className="seller-form-label">
                    URL di base PrestaShop:
                  </label>
                  <input
                    type="text"
                    className="seller-form-control"
                    name="prestashopProductBaseUrl"
                    value={formData.prestashopProductBaseUrl}
                    onChange={handleChange}
                    placeholder="https://tuo-negozio.com/prodotto"
                  />
                </div>
              </div>
            )}

            {formData.integrationMethod === 'woocommerce' && (
              <div className="seller-integration-form-group">
                <div className="seller-form-group">
                  <label className="seller-form-label">
                    WooCommerce URL:
                  </label>
                  <input
                    type="url"
                    className="seller-form-control"
                    name="woocommerceUrl"
                    value={formData.woocommerceUrl}
                    onChange={handleChange}
                    placeholder="https://tuo-negozio.com"
                  />
                </div>
                <TokenInput
                  name="woocommerceApiKey"
                  value={formData.woocommerceApiKey}
                  onChange={handleTokenChange}
                  placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  label="WooCommerce API Key:"
                  type="text"
                />
                <TokenInput
                  name="woocommerceApiSecret"
                  value={formData.woocommerceApiSecret}
                  onChange={handleTokenChange}
                  placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  label="WooCommerce API Secret:"
                />
                <div className="seller-form-group">
                  <label className="seller-form-label">
                    URL di base prodotto WooCommerce:
                  </label>
                  <input
                    type="text"
                    className="seller-form-control"
                    name="woocommerceProductBaseUrl"
                    value={formData.woocommerceProductBaseUrl}
                    onChange={handleChange}
                    placeholder="https://tuo-negozio.com/prodotto"
                  />
                </div>
              </div>
            )}

            {formData.integrationMethod === 'magento' && (
              <div className="seller-integration-form-group">
                <div className="seller-form-group">
                  <label className="seller-form-label">
                    Magento URL:
                  </label>
                  <input
                    type="url"
                    className="seller-form-control"
                    name="magentoUrl"
                    value={formData.magentoUrl}
                    onChange={handleChange}
                    placeholder="https://tuo-negozio.com"
                  />
                </div>
                <TokenInput
                  name="magentoAccessToken"
                  value={formData.magentoAccessToken}
                  onChange={handleTokenChange}
                  placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  label="Magento Access Token:"
                />
                <div className="seller-form-group">
                  <label className="seller-form-label">
                    URL di base prodotto Magento:
                  </label>
                  <input
                    type="text"
                    className="seller-form-control"
                    name="magentoProductBaseUrl"
                    value={formData.magentoProductBaseUrl}
                    onChange={handleChange}
                    placeholder="https://tuo-negozio.com/prodotto/"
                  />
                </div>
              </div>
            )}

            {/* Azioni */}
            <div className="seller-integration-form-actions">
              <button
                type="button"
                onClick={handleSaveIntegration}
                className="seller-btn seller-btn-success"
              >
                Salva Configurazione
              </button>
              <button
                type="button"
                onClick={handleClearIntegration}
                className="seller-btn seller-btn-outline-danger"
              >
                Reset
              </button>
            </div>
          </form>
        ) : (
          <div className="seller-integration-info-grid">
            <div className="seller-integration-info-card">
              <div className="seller-integration-info-label">Metodo di Integrazione</div>
              <div className="seller-integration-info-value">
                {sellerInfo?.integrationMethod || 'N/A'}
              </div>
            </div>

            {/* Sezione Status Import */}
            <div className="seller-integration-info-card">
              <div className="seller-integration-info-label">Status Import</div>
              <div className="seller-integration-info-value">
                <span className={`status-badge status-${sellerInfo?.import_status || 'idle'}`}>
                  {sellerInfo?.import_status === 'in_progress' && 'In Corso'}
                  {sellerInfo?.import_status === 'completed' && 'Completato'}
                  {sellerInfo?.import_status === 'error' && 'Errore'}
                  {sellerInfo?.import_status === 'idle' && 'In Attesa'}
                </span>
              </div>
            </div>

            {sellerInfo?.last_import_start && (
              <div className="seller-integration-info-card">
                <div className="seller-integration-info-label">Ultimo Import Iniziato</div>
                <div className="seller-integration-info-value">
                  {new Date(sellerInfo.last_import_start).toLocaleString('it-IT')}
                </div>
              </div>
            )}

            {sellerInfo?.last_import_end && (
              <div className="seller-integration-info-card">
                <div className="seller-integration-info-label">Ultimo Import Completato</div>
                <div className="seller-integration-info-value">
                  {new Date(sellerInfo.last_import_end).toLocaleString('it-IT')}
                </div>
              </div>
            )}

            {sellerInfo?.import_error && (
              <div className="seller-integration-info-card">
                <div className="seller-integration-info-label">Ultimo Errore</div>
                <div className="seller-integration-info-value error-text">
                  {sellerInfo.import_error}
                </div>
              </div>
            )}

            {sellerInfo?.import_frequency && (
              <div className="seller-integration-info-card">
                <div className="seller-integration-info-label">Frequenza Import</div>
                <div className="seller-integration-info-value">
                  Ogni {sellerInfo.import_frequency} ore
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Danger zone: elimina integrazione + prodotti associati */}
      <div className="seller-card seller-danger-zone">
        <div className="seller-danger-zone-header">
          <h3>⚠️ Elimina integrazione</h3>
        </div>
        <p className="seller-danger-zone-text">
          Rimuove la configurazione dell'integrazione e <strong>cancella
          definitivamente</strong> tutti i prodotti importati da questo
          connettore. L'operazione è <strong>irreversibile</strong>.
          <br />
          <small>
            I prodotti eventualmente caricati tramite <strong>API Push</strong> non
            vengono toccati.
          </small>
        </p>
        <button
          type="button"
          className="seller-btn seller-btn-danger"
          onClick={() => setShowDeleteModal(true)}
          disabled={sellerInfo?.import_status === 'in_progress'}
        >
          Elimina integrazione
        </button>
        {sellerInfo?.import_status === 'in_progress' && (
          <p className="seller-danger-zone-note">
            Non è possibile eliminare durante un aggiornamento in corso.
          </p>
        )}
      </div>

      {/* Modale di conferma con digitazione */}
      {showDeleteModal && (
        <div className="seller-modal-overlay" onClick={closeDeleteModal}>
          <div className="seller-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="seller-modal-title">⚠️ Conferma eliminazione</h3>
            <p className="seller-modal-text">
              Stai per eliminare l'integrazione <strong>{sellerInfo?.integrationMethod}</strong> e
              cancellare definitivamente tutti i suoi prodotti. Questa azione
              non può essere annullata.
            </p>
            <p className="seller-modal-text">
              Per confermare, digita <strong>{DELETE_CONFIRM_WORD}</strong> qui sotto:
            </p>
            <input
              type="text"
              className="seller-form-control"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={DELETE_CONFIRM_WORD}
              autoFocus
              disabled={deleting}
            />
            <div className="seller-modal-actions">
              <button
                type="button"
                className="seller-btn seller-btn-secondary"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Annulla
              </button>
              <button
                type="button"
                className="seller-btn seller-btn-danger"
                onClick={handleDeleteIntegration}
                disabled={deleting || deleteConfirmText.trim() !== DELETE_CONFIRM_WORD}
              >
                {deleting ? 'Eliminazione in corso...' : 'Conferma eliminazione'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SellerIntegration;